/**
 * Default cap on poll attempts. At a 5s interval that is ~25 minutes.
 */
export const DEFAULT_MAX_POLL_ATTEMPTS = 300;

/**
 * Drop-in replacement for `setInterval` for polls that hit the network.
 *
 * Adds the two things none of the app's polling loops had:
 *
 * 1. **Visibility gating** - the callback does not run while the tab is hidden,
 *    so a backgrounded tab stops generating traffic. The interval itself keeps
 *    ticking, so polling resumes by itself when the user comes back; no
 *    listener to register, and no state to restore.
 * 2. **An attempt cap** - a job that never reaches a terminal status stops
 *    being polled rather than running for the life of the session. Skipped
 *    ticks are not counted, so a tab left in the background does not burn
 *    through the cap.
 *
 * Returns a real interval id, so existing `clearInterval(ref.current)` cleanup
 * keeps working untouched.
 *
 * For new code prefer `hooks/useAsyncJobPoll`, which owns the whole lifecycle.
 * This exists to retrofit the many loops whose surrounding logic is bespoke.
 *
 * @param {() => void} fn one poll
 * @param {number} intervalMs gap between polls
 * @param {Object} [options]
 * @param {number} [options.maxAttempts]
 * @param {() => void} [options.onExhausted] called once when the cap is hit
 * @returns {number} interval id, for `clearInterval`
 */
export const startPolling = (fn, intervalMs, options = {}) => {
  const { maxAttempts = DEFAULT_MAX_POLL_ATTEMPTS, onExhausted } = options;
  let attempts = 0;
  let id = null;

  const tick = () => {
    if (document.hidden) return;

    attempts += 1;
    if (attempts > maxAttempts) {
      clearInterval(id);
      onExhausted?.();
      return;
    }
    fn();
  };

  id = setInterval(tick, intervalMs);
  return id;
};

export default startPolling;
